import { useEffect, type ReactNode } from "react";
import { Icon } from "../Icon";

function useEscapeToClose(onClose: () => void) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      onClick={onClose}
      aria-label="Close"
      style={{
        width: 34,
        height: 34,
        borderRadius: 99,
        border: "none",
        background: "var(--surface-inset)",
        color: "var(--text-muted)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
      }}
    >
      <Icon name="x" size={17} />
    </button>
  );
}

/** Centred modal dialog with the kd-pop animation. */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  width = 560,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  useEscapeToClose(onClose);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(15,14,34,.45)", animation: "kd-fade .2s ease" }} />
      <div
        className="kd-modal"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: width,
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--surface)",
          border: "var(--border-w) solid var(--border)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "var(--border-w) solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "var(--text)" }}>{title}</h2>
            {subtitle && <p style={{ margin: "4px 0 0", fontSize: 13.5, color: "var(--text-muted)" }}>{subtitle}</p>}
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <div style={{ padding: 24, overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
}

/** Right-edge slide-over panel with the kd-slide-in animation. */
export function SlideOver({
  title,
  onClose,
  children,
  width = 440,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  useEscapeToClose(onClose);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} style={{ position: "fixed", inset: 0, zIndex: 100 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(15,14,34,.4)", animation: "kd-fade .2s ease" }} />
      <aside
        className="kd-slideover"
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          width: "100%",
          maxWidth: width,
          background: "var(--surface)",
          borderLeft: "var(--border-w) solid var(--border)",
          boxShadow: "var(--shadow-lg)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "var(--border-w) solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{title}</h2>
          <CloseButton onClose={onClose} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 22 }}>{children}</div>
      </aside>
    </div>
  );
}
